import { useCallback } from "react";
import { useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { useNavigate } from "react-router-dom";
import { buildMessagingThreadLink } from "../utils/build-messaging-thread-link";
import { useUpdateEnduserInSidebar } from "./useUpdateEnduserInSidebar";

/**
 * Hook that returns a function to open a Chat thread for a given chatroom.
 *
 * This hook:
 * 1. Gets the queryClient and navigate internally
 * 2. Returns a callback that fetches the thread and navigates to it
 * 3. Queries for the matching thread using load_threads with filters
 * 4. Navigates to the thread URL if found
 *
 * @returns A function that accepts a chatroom id and opens its thread, resolving `true` once it has
 * navigated. Resolves `false` when the room has no inbox thread — a room whose only message is
 * scheduled or a draft has no sent message for the server to build one from — letting callers offer
 * an alternative to navigation instead of appearing to do nothing.
 */
export const useOpenChatThread = () => {
  const queryClient = useTellescopeQueryClient();
  const navigate = useNavigate();
  const updateEnduserInSidebar = useUpdateEnduserInSidebar();

  const openChatThread = useCallback(
    async (roomId: string, draftId?: string): Promise<boolean> => {
      try {
        // Fetch the thread using load_threads
        const result = await queryClient.fetchInfiniteQuery({
          model: "inbox_threads",
          method: "load_threads",
          request: {
            autobuild: true,
            limit: 1,
            mdbFilter: {
              type: "Chat",
              threadId: roomId,
            },
          },
          getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
          initialPageParam: undefined,
          paginateRequest: (lastTimestamp, request) => ({ ...request, lastTimestamp }),
        });

        // Extract the first thread from the result
        const thread = result.pages[0]?.threads[0];

        if (!thread) {
          return false;
        }

        // Navigate to the thread
        const threadLink = buildMessagingThreadLink({
          view: "inbox",
          channel: "Chat",
          threadId: thread.id,
          draftId,
        });

        const enduserId = thread.enduserIds?.[0];
        updateEnduserInSidebar(enduserId);

        navigate(threadLink);
        return true;
      } catch (error) {
        console.error("Error opening chat thread:", error);
        return false;
      }
    },
    [navigate, queryClient, updateEnduserInSidebar]
  );

  return openChatThread;
};
